/**
 * Creators dashboard — two tabs:
 *
 *   Creator dashboards — create and manage the persistent, passcode-protected
 *                        payout report pages for creators and agencies at
 *                        culturegenesis.com/creators/{slug}/reports (plus
 *                        their Tentpole Calendar).
 *   Brand logos        — the advertiser logos those report pages show, stored
 *                        on the Sales advertiser registry.
 *
 * ?tab=<key> selects a tab (missing/unknown values land on Creator
 * dashboards). Each tab stays mounted once visited and is hidden, not
 * unmounted, on a switch: the dashboards list is slow and the create modal
 * keeps a draft; the logos tab keeps its staged suggestions. The dashboards
 * tab keeps its search and filters in the URL (q / type / status); the logos
 * tab's are its own state.
 *
 * Gated on clientReports.manage ("Creators Dashboard") in App.tsx; the API
 * enforces the same key. Moved out of Takeovers in Oct 2026 — old
 * ?tab=creator-pages links are redirected here by TakeoversRoute.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, DashboardFrame, Tabs } from '../ui';
import { DASHBOARDS } from '../../config/dashboards';
import { useDashboardToast } from '../../hooks/useDashboardToast';
import CreatorDashboardsTab from './CreatorDashboardsTab';
import BrandLogosTab from './BrandLogosTab';
import {
  CREATORS_DASHBOARD_TAB_ORDER,
  CreatorsDashboardTabKey as TabKey,
  resolveInitialCreatorsDashboardTab,
} from './creatorsDashboardTabs';

/** The launcher card's description doubles as the in-page subtitle. */
const SUBTITLE = DASHBOARDS.find((d) => d.id === 'creators')?.description;

const TAB_LABELS: Record<TabKey, string> = {
  dashboards: 'Creator dashboards',
  'brand-logos': 'Brand logos',
};

const CreatorsDashboard: React.FC = () => {
  const { notify, toastLayer } = useDashboardToast();
  const [showCreate, setShowCreate] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<TabKey>(() => resolveInitialCreatorsDashboardTab(searchParams));
  const [visited, setVisited] = useState<Record<TabKey, boolean>>(() => ({
    dashboards: tab === 'dashboards',
    'brand-logos': tab === 'brand-logos',
  }));

  const changeTab = useCallback(
    (next: TabKey) => {
      setTab(next);
      setVisited((v) => (v[next] ? v : { ...v, [next]: true }));
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set('tab', next);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const tabOptions = useMemo(() => CREATORS_DASHBOARD_TAB_ORDER.map((key) => ({ value: key, label: TAB_LABELS[key] })), []);

  /** Mounted once visited; hidden (not unmounted) while another tab is showing. */
  const panel = (key: TabKey, content: React.ReactNode) =>
    visited[key] && (
      <div style={tab === key ? undefined : { display: 'none' }} aria-hidden={tab !== key}>
        {content}
      </div>
    );

  return (
    <DashboardFrame
      eyebrow="CG Core"
      title="Creators"
      subtitle={SUBTITLE}
      tabs={<Tabs value={tab} onChange={changeTab} options={tabOptions} variant="underlined" ariaLabel="Creators sections" />}
      topBarRight={
        tab === 'dashboards' ? (
          <Button variant="primary" size="sm" onClick={() => setShowCreate(true)}>
            + New creator dashboard
          </Button>
        ) : undefined
      }
    >
      {panel('dashboards', <CreatorDashboardsTab notify={notify} showCreate={showCreate} onShowCreateChange={setShowCreate} />)}
      {panel('brand-logos', <BrandLogosTab notify={notify} active={tab === 'brand-logos'} />)}

      {/* Fixed above the modal layer: the create/edit/reset modals raise most of these. */}
      {toastLayer}
    </DashboardFrame>
  );
};

export default CreatorsDashboard;
